import { Button, Card, Tab, Tabs } from "@blueprintjs/core"
import CustomButton from "components/Atoms/CustomButton"
import CustomFormGroup from "components/Atoms/CustomFormGroup"
import StringInput from "components/Atoms/StringInput"
import DynamicSelector from "components/Molecules/DynamicSelector"
import FormController from "components/Molecules/FormController"
import MutationOptions from "components/Organisms/MutationOptions"
import GoogleMapDialog from "components/Templates/GoogleMapDialog"
import { ADD_PLOT, PLOT_DETAILS } from "graphql/plot.graphql"
import { COMPANIES, SOIL_TYPES } from "graphql/selector.queries.gql"
import { useFormDisabled } from "hooks/useAuth"
import useErrors from "hooks/useErrors"
import useRouteForm from "hooks/useRouteForm"
import { cloneDeep, get, set, update } from "lodash-es"
import plotModel from "models/molecules/plot.model"
import React, { useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import AppToaster, { ApolloErrorToast } from "utils/AppToaster"
import { removeTypename } from "utils/objectUtils"
import Crop from "./Crop"
import { omit } from "lodash-es"
import { BlockUserNote } from "components/Common/BlockUserNote"

function cropModel() {
  return {
    variety: null,
    sowingDate: null,
    harvestingDate: null,
    growingCycle: 0,
    numberOfSaplings: 0,
    generatePoPlotWise: false,
    agronomist: null,
  }
}

const showSuccessToast = data => {
  const plot = get(data, "addUpdateOnlyPlot.plot", {})
  AppToaster.show({
    message: `Plot ${plot.name} added successfully`,
    intent: "success",
  })
}

// Plot Regstration Component
const PlotRegistration = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [plot, changePlot] = useRouteForm(PLOT_DETAILS, {
    default: {
      farmer: location.state,
      crops: [{}],
    },
    transform(state) {
      return removeTypename({
        ...state,
      })
    },
  })

  const parsedVariables = useMemo(() => {
    return {
      ...plot,
      farmer: plot?.farmer?.id,
      soilType: plot?.soilType?.id,
      company: plot?.company?.id,
      crops: plot?.crops?.map(c => {
        return {
          ...omit(c, "referenceId"),
          agronomist: c?.agronomist?.id,
          // Ignore isArchived (since undefined values are not sent through)
          isArchived: undefined,
          variety: c?.variety?.id,
          alternateCropGroupId: c?.alternateCropGroupId?.id || null,
        }
      }),
    }
  }, [plot])

  const [errors] = useErrors({ schema: plotModel, state: parsedVariables })

  const [selectedCrop, changeSelectedCrop] = useState(
    plot.crops && plot.crops.length > 0 ? plot.crops.length - 1 : 0
  )

  const handleChange = (path, value) => {
    const cropPath = path
      .split(".")
      .filter((_, idx) => idx < 2)
      .join(".")

    const plotCopy = cloneDeep(plot)
    if (/isRawMaterial/.test(path)) {
      update(plotCopy, cropPath + ".isFinishedGood", v => (value ? false : v))
    } else if (/isFinishedGood/.test(path)) {
      update(plotCopy, cropPath + ".isRawMaterial", v => (value ? false : v))
    }

    if (/variety|isRawMaterial|isFinishedGood/.test(path)) {
      // Reset alternate group ID if any of the above value changes becuase alternative groups are dependendent on above values
      set(plotCopy, cropPath + ".alternateCropGroupId", null)
    }
    set(plotCopy, path, value)

    changePlot(plotCopy)
  }
  const handleAdd = e => {
    const plotCopy = cloneDeep(plot)
    update(plotCopy, "crops", crops => crops.concat(cropModel()))
    changePlot(plotCopy)
    changeSelectedCrop(plotCopy.crops.length - 1)
  }

  const handleRemove = index => {
    const plotCopy = cloneDeep(plot)
    update(plotCopy, "crops", crops => crops.filter((_, i) => i !== index))
    changeSelectedCrop(plotCopy.crops.length - 1)
    changePlot(plotCopy)
  }

  const disabled = useFormDisabled("plot", parsedVariables)

  return (
    <Card className="vt-plot-registration">
      <div className="mt-2 w-full flex justify-between">
        <span>
          <Button
            icon="arrow-left"
            onClick={() => {
              navigate(-1)
            }}
            minimal={true}
          ></Button>
          <h5 className="ml-3" style={{ display: "inline-block" }}>
            {plot.id ? "Update" : "New"} Plot
          </h5>
        </span>
      </div>
      <div className="container mt-3">
        <h5>
          <span className="text-muted">Farmer - </span> {plot?.farmer?.fullName}{" "}
          <span className="text-muted">({plot?.farmer?.username})</span>
        </h5>
        <hr className="mt-0 mb-3" />
        {
          plot?.farmer?.isBlocked ? (
            <BlockUserNote remark={plot?.farmer?.blockRemark} />
          ) : null
        }

        <FormController
          errors={errors.details}
          source={plot}
          onChange={handleChange}
          disabled={disabled || plot?.farmer?.isBlocked}
          disableAll={true}
        >
          <div className="grid grid-cols-4 gap-4">
            <StringInput
              path="name"
              label="Plot Name"
              helperText="Identifiable Tag For Plot"
              required
            />
            <DynamicSelector
              required
              big={true}
              query={COMPANIES}
              path="company"
              label="Company"
              helperText="eg. SFPCL"
            />
            <StringInput
              path="area"
              type="number"
              label="Plot Area"
              labelInfo={<span className="vt-label-muted">(Acres)</span>}
              helperText="Total area of the land (Acres.)"
              required
            />

            <CustomFormGroup big label="Address" required>
              <GoogleMapDialog
                // disabled={true}
                btnProps={{
                  big: true,
                  fill: true,
                  intent: "none",
                  text: "Select Address",
                }}
                path="address"
              />
            </CustomFormGroup>
          </div>
          <div className="grid grid-cols-4 gap-4 mt-3">
            <StringInput path="codename" label="Codename" disabled={true} />
            <DynamicSelector
              big={true}
              query={SOIL_TYPES}
              path="soilType"
              label="Soil Type"
            />
            <StringInput required path="gatNumber" label="Gat Number" />
          </div>
          <h6 className="mt-6 vt-section-header">Crop Details</h6>
          <hr className="mt-0" />
          <Tabs
            selectedTabId={selectedCrop}
            onChange={id => {
              if (id === selectedCrop && plot.crops.length - 1) {
                changeSelectedCrop(id - 1)
              } else {
                changeSelectedCrop(Number(id))
              }
            }}
            className="vt-plot-crops"
          >
            {plot?.crops?.map((crop, index) => {
              const groups = crop?.variety?.groups?.filter(g => {
                if (crop?.isFinishedGood) {
                  return g?.isFinishedGood
                } else if (crop?.isRawMaterial) {
                  return g?.isRawMaterial
                } else {
                  return false
                }
              })
              return (
                <Tab
                  id={index}
                  title={get(crop, "variety.name", `Crop ${index + 1}`)}
                  key={index}
                  panel={
                    <Crop
                      id={index}
                      isArchived={crop.isArchived}
                      groups={groups}
                    />
                  }
                >
                  {crop.id ? null : (
                    <CustomButton
                      minimal={true}
                      icon="cross"
                      intent="danger"
                      onClick={e => handleRemove(index)}
                    />
                  )}
                </Tab>
              )
            })}
            <CustomButton
              disabled={disabled || plot?.farmer?.isBlocked}
              minimal={true}
              icon="plus"
              onClick={handleAdd}
            />
          </Tabs>
        </FormController>

        <div className="flex justify-end mt-5">
          <MutationOptions
            disabled={disabled || plot?.farmer?.isBlocked}
            mutation={ADD_PLOT}
            variables={{ plot: parsedVariables }}
            validate={() => {
              if (errors.latest) {
                AppToaster.show({
                  message: errors.latest,
                  intent: "danger",
                  icon: "cross",
                })
                return false
              }
              return true
            }}
            update={(cache, { data }) => {
              const plot = data.addUpdateOnlyPlot.plot
              cache.modify({
                id: `BaseUserType:${plot.farmer.id}`,
                fields: {
                  plots(existingPlots = []) {
                    return [
                      {
                        __ref: `PlotType:${plot.id}`,
                      },
                    ].concat(
                      existingPlots.filter(p => !p.__ref.includes(plot.id))
                    )
                  },
                },
              })
            }}
            onError={ApolloErrorToast}
            onSave={data => {
              showSuccessToast(data)
              navigate(-1)
            }}
            onSaveAndAdd={data => {
              showSuccessToast(data)
              const farmer = get(data, "addUpdateOnlyPlot.plot.farmer")
              changePlot({ farmer, crops: [{}] })
              navigate("/d/registration/plot/new", { replace: true })
            }}
            onSaveAndEdit={data => {
              showSuccessToast(data)
              navigate(
                `/d/registration/plot/${data.addUpdateOnlyPlot.plot.id}`,
                { replace: true }
              )
            }}
          ></MutationOptions>
        </div>
      </div>
    </Card>
  )
}

export default PlotRegistration
